// Core payroll system types - country-agnostic definitions

import { Employee } from '@/types';

// Generic country code type - extendable
export type CountryCode = string;

// Generic contract type - each country can define their own
export type GenericContractType = string;

// Generic employment classification
export type EmploymentClassification = 'PERMANENT' | 'TEMPORARY' | 'CONTRACT' | 'INTERN' | 'PART_TIME' | 'FULL_TIME';

// Generic insurance/contribution types
export type InsuranceType = 'PENSION' | 'DISABILITY' | 'SICKNESS' | 'UNEMPLOYMENT' | 'ACCIDENT' | 'OTHER';

// Generic deduction types  
export type DeductionType = 'TAX' | 'SOCIAL_INSURANCE' | 'HEALTH_INSURANCE' | 'PENSION_CONTRIBUTION' | 'OTHER';

// Base payroll period
export interface PayrollPeriod {
  start: string;
  end: string;
  month: number;
  year: number;
  countryCode: CountryCode;
}

// Generic payroll input data
export interface PayrollInputData {
  employeeId: string;
  countryCode: CountryCode;
  period: PayrollPeriod;
  
  // Planning vs Actual mode
  isPlanning?: boolean; // If true, use planned hours; if false, use actual hours
  
  // Time tracking (actual hours worked)
  regularHours: number;
  overtimeHours: number;
  specialHours: Record<string, number>; // night, weekend, holiday, etc.
  
  // Planning data (scheduled hours)
  plannedRegularHours?: number;
  plannedOvertimeHours?: number;
  plannedSpecialHours?: Record<string, number>;
  contractedHoursPerMonth?: number; // For salary/fixed contract employees
  
  // Absences
  vacationDays: number;
  sickLeaveDays: number;
  otherAbsences: Record<string, number>;
  
  // Financial additions
  bonuses: number;
  commissions: number;
  otherIncome: Record<string, number>;
  
  // Deductions
  advances: number;
  otherDeductions: Record<string, number>;
  
  // Country-specific data
  countrySpecificData?: Record<string, any>;
}

// Generic gross pay components
export interface GrossPayComponents {
  basePay: number;
  overtimePay: number;
  specialPay: Record<string, number>;
  absencePay: Record<string, number>;
  bonuses: number;
  otherIncome: number;
  total: number;
}

// Generic insurance calculation
export interface InsuranceCalculation {
  employee: Record<InsuranceType | string, number>;
  employer: Record<InsuranceType | string, number>;
  employeeTotal: number;
  employerTotal: number;
}

// Generic tax calculation
export interface TaxCalculation {
  taxBase: number;
  taxableIncome: number;
  allowances: Record<string, number>;
  taxBrackets: Record<string, number>;
  credits: Record<string, number>;
  totalTax: number;
  netTaxBenefit: number; // For cases where credits exceed tax
}

// Generic deduction calculation  
export interface DeductionCalculation {
  type: DeductionType;
  amount: number;
  description: string;
  mandatory: boolean;
}

// Generic payroll calculation result
export interface PayrollCalculation {
  employee: Employee;
  period: PayrollPeriod;
  inputData: PayrollInputData;
  
  // Calculations
  grossComponents: GrossPayComponents;
  insuranceCalculations: Record<string, InsuranceCalculation>;
  taxCalculation: TaxCalculation;
  deductions: DeductionCalculation[];
  
  // Final amounts
  grossSalary: number;
  netSalary: number;
  finalPayout: number;
  totalEmployerCost: number;
  
  // Metadata
  calculationDate: string;
  calculationEngine: string;
  countrySpecificData?: Record<string, any>;
}

// Country-specific payroll constants interface
export interface CountryPayrollConstants {
  countryCode: CountryCode;
  year: number;
  currency: string;
  
  // Minimum wage
  minimumWageHourly: number;
  minimumWageMonthly: number;
  minimumWageAnnual: number;
  
  // Working time limits
  standardHoursPerWeek: number;
  maxHoursPerWeek: number;
  maxHoursPerDay: number;
  
  // Tax system
  taxSystem: 'PROGRESSIVE' | 'FLAT' | 'MIXED';
  taxBrackets: Array<{
    min: number;
    max: number | null;
    rate: number;
  }>;
  
  // Standard allowances
  personalAllowance: number;
  dependentAllowances: Record<string, number>;
  
  // Insurance rates
  socialInsuranceRates: {
    employee: Record<string, number>;
    employer: Record<string, number>;
  };
  
  healthInsuranceRates: {
    employee: number;
    employer: number;
  };
  
  // Assessment limits
  maxAssessmentBase: number;
  minAssessmentBase: number;
  
  // Country-specific constants
  countrySpecificConstants: Record<string, any>;
  
  // Validation rules
  validationRules: CountryValidationRules;
}

// Country-specific validation rules
export interface CountryValidationRules {
  mandatoryEmployeeFields: string[];
  employeeFieldValidators: Record<string, {
    type: 'string' | 'number' | 'date' | 'email' | 'custom';
    pattern?: string;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    customValidator?: string; // Function name for custom validation
  }>;
  contractTypes: string[];
  payFrequencies: string[];
  specialRules?: Record<string, any>;
}

// Payroll engine error types
export class PayrollEngineError extends Error {
  constructor(
    public countryCode: CountryCode,
    public errorCode: string,
    message: string,
    public details?: any
  ) {
    super(message);
    this.name = 'PayrollEngineError';
  }
}

export class ValidationError extends PayrollEngineError {
  constructor(
    countryCode: CountryCode,
    message: string,
    public fieldErrors: Record<string, string[]>,
    details?: any
  ) {
    super(countryCode, 'VALIDATION_ERROR', message, details);
    this.name = 'ValidationError';
  }
}

export class CalculationError extends PayrollEngineError {
  constructor(
    countryCode: CountryCode,
    message: string,
    public step: string,
    details?: any
  ) {
    super(countryCode, 'CALCULATION_ERROR', message, details);
    this.name = 'CalculationError';
  }
}